import { scissorsClient as defaultScissorsClient } from '@neptune/shared/core-apis-scissors-domain';

import { hashString as defaultHashString } from './hash-string';

const defaultCache = new Map<string, unknown>();

type Dependencies = {
  cache: Map<string, unknown>;
  scissorsClient: typeof defaultScissorsClient;
  hashString: typeof defaultHashString;
};

type SerializableObject = Record<string, unknown> | unknown[];

export const createKeyValueStore = (dependencies: Partial<Dependencies> = {}) => {
  const {
    cache = defaultCache,
    scissorsClient = defaultScissorsClient,
    hashString = defaultHashString,
  } = dependencies;

  async function persistObject(serializableObject: SerializableObject): Promise<string> {
    return persistObjectWithOptions(serializableObject, { waitForRemotePersist: false });
  }

  async function persistObjectAndWait(serializableObject: SerializableObject): Promise<string> {
    return persistObjectWithOptions(serializableObject, { waitForRemotePersist: true });
  }

  async function persistObjectWithOptions(
    serializableObject: SerializableObject,
    { waitForRemotePersist }: { waitForRemotePersist: boolean },
  ): Promise<string> {
    const content = JSON.stringify(serializableObject);
    const key = await hashString(content);

    if (waitForRemotePersist) {
      await persistString(content);
    } else {
      fireAndForgetPersistString(content);
    }

    cache.set(key, serializableObject);

    return key;
  }

  function fireAndForgetPersistString(content: string) {
    void persistString(content).catch(() => undefined);
  }

  async function persistString(content: string): Promise<void> {
    await scissorsClient.snip({
      snipRequest: {
        content,
      },
    });
  }

  async function loadObject<T>(
    key: string,
    typeGuard: (value: unknown) => value is T,
  ): Promise<T | undefined> {
    if (cache.has(key)) {
      const cached = cache.get(key);
      return typeGuard(cached) ? cached : undefined;
    }

    try {
      const { content } = await scissorsClient.expand({ key });
      const parsed = JSON.parse(content);

      return typeGuard(parsed) ? parsed : undefined;
    } catch {
      /* ignore exceptions */
    }
  }

  return {
    persistObject,
    persistObjectAndWait,
    loadObject,
  };
};

export const keyValueStore = createKeyValueStore();
